import { window } from "./window.util";

/**
 * Dynamically loads an external script.
 * @param url The URL of the script to load.
 * @returns A promise that resolves when the script is loaded.
 */
function loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
        // Check if the script is already loaded
        const existingScript = Array.from(document.getElementsByTagName('script')).find(
            (script) => script.src === url
        );
        if (existingScript) {
            console.log(`Script already loaded: ${url}`);
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => {
            console.log(`Successfully loaded script: ${url}`);
            resolve();
        };
        script.onerror = () => {
            reject(new Error(`Failed to load script: ${url}`));
        };
        document.head.appendChild(script);
    });
}

/**
 * Delays execution for a specified amount of time.
 * @param ms Milliseconds to delay.
 * @returns A promise that resolves after the delay.
 */
function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generates a PDF Blob from a specific DOM element, setting the PDF size based on the element's dimensions.
 * @param element The DOM element to convert to PDF.
 * @returns A promise that resolves with the PDF Blob or null if an error occurs.
 */
async function generatePDFBlob(element: HTMLElement): Promise<Blob | null> {
    if (!window.html2pdf) {
        console.error('html2pdf is not loaded.');
        return null;
    }

    // Retrieve the element's dimensions in pixels
    const rect = element.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const opt = {
        margin: 0.5,
        filename: 'temp.pdf', // Temporary name; actual name will be set in the ZIP
        image: { type: 'jpeg', quality: 0.98 },
        pagebreak: {
            mode: 'avoid-all',
        },
        html2canvas: {
            orientation: 'portrait',
            useCORS: true,
            allowTaint: false,
            scale: 2, // Adjust scale for higher resolution if needed
        },
        jsPDF: {
            orientation: 'portrait',
            unit: 'px', // Using pixels as units
            format: [width, height], // Set format based on element's width and height
            hotfixes: ['px_scaling'], // Ensure proper scaling with 'px' units
        },
    };

    try {
        // Initialize html2pdf with the specified options and generate the PDF as a Blob
        const pdf: any = window.html2pdf().set(opt).from(element).output('blob');
        return await pdf;
    } catch (error) {
        console.error('Error generating PDF:', error);
        return null;
    }
}

/**
 * Creates and displays a modal with a progress bar.
 * @returns An object containing functions to update the progress and close the modal.
 */
function createProgressModal() {
    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'progress-modal-overlay';
    Object.assign(overlay.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: '10000',
    });

    // Create modal container
    const modal = document.createElement('div');
    modal.id = 'progress-modal';
    Object.assign(modal.style, {
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        width: '400px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
    });

    // Create title
    const title = document.createElement('h2');
    title.innerText = 'Processing Routes';
    Object.assign(title.style, {
        marginBottom: '20px',
        fontSize: '18px',
    });

    // Create status message
    const status = document.createElement('p');
    status.id = 'progress-status';
    status.innerText = 'Initializing...';
    Object.assign(status.style, {
        marginBottom: '20px',
        fontSize: '14px',
    });

    // Create progress bar container
    const progressBarContainer = document.createElement('div');
    Object.assign(progressBarContainer.style, {
        width: '100%',
        backgroundColor: '#e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden',
        height: '20px',
        marginBottom: '20px',
    });

    // Create progress bar
    const progressBar = document.createElement('div');
    progressBar.id = 'progress-bar';
    Object.assign(progressBar.style, {
        height: '100%',
        width: '0%',
        backgroundColor: '#F6961D',
        transition: 'width 0.3s ease',
    });

    // Append progress bar to container
    progressBarContainer.appendChild(progressBar);

    // Create cancel button
    const cancelButton = document.createElement('button');
    cancelButton.innerText = 'Cancel';
    Object.assign(cancelButton.style, {
        padding: '10px 20px',
        backgroundColor: '#D8090F',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    });

    // Append elements to modal
    modal.appendChild(title);
    modal.appendChild(status);
    modal.appendChild(progressBarContainer);
    modal.appendChild(cancelButton);

    // Append modal to overlay
    overlay.appendChild(modal);

    // Append overlay to body
    document.body.appendChild(overlay);

    // Return functions to update progress and status, and to close the modal
    return {
        updateProgress: (percent: number) => {
            const progressBar = document.getElementById('progress-bar') as HTMLElement;
            if (progressBar) {
                progressBar.style.width = `${percent}%`;
            }
        },
        updateStatus: (message: string) => {
            const status = document.getElementById('progress-status') as HTMLElement;
            if (status) {
                status.innerText = message;
            }
        },
        close: () => {
            const overlay = document.getElementById('progress-modal-overlay');
            if (overlay) {
                overlay.remove();
            }
        },
    };
}

/**
 * Converts camelCase strings to space-separated words with each word capitalized.
 * 
 * @param str - The camelCase string to convert.
 * @returns The converted string with spaces and capitalized words.
 */
function camelCaseToSpaces(str: string): string {
    let spaced = str.replace(/([a-z])([A-Z])/g, '$1 $2');

    spaced = spaced.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');

    const capitalized = spaced.replace(/\b\w/g, char => char.toUpperCase());

    return capitalized;
}

/**
 * Displays a confirmation popup to the user.
 * @returns A promise that resolves to true if the user confirms, or false otherwise.
 */
function showConfirmationPopup(): Promise<boolean> {
    return new Promise((resolve) => {
        // Create overlay
        const overlay = document.createElement('div');
        overlay.id = 'confirmation-popup-overlay';
        Object.assign(overlay.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: '10001',
        });

        // Create modal container
        const modal = document.createElement('div');
        modal.id = 'confirmation-popup';
        Object.assign(modal.style, {
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            width: '400px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            textAlign: 'center',
            fontFamily: 'Arial, sans-serif',
        });

        // Create message
        const message = document.createElement('p');
        message.innerText = 'This process could take a few minutes. Are you sure you want to proceed?';
        Object.assign(message.style, {
            marginBottom: '20px',
            fontSize: '14px',
        });

        // Create buttons container
        const buttonsContainer = document.createElement('div');
        Object.assign(buttonsContainer.style, {
            display: 'flex',
            justifyContent: 'space-around',
        });

        // Create Confirm button
        const confirmButton = document.createElement('button');
        confirmButton.innerText = 'Yes';
        Object.assign(confirmButton.style, {
            padding: '10px 40px',
            backgroundColor: '#07B824',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
        });

        // Create Cancel button
        const cancelButton = document.createElement('button');
        cancelButton.innerText = 'No';
        Object.assign(cancelButton.style, {
            padding: '10px 40px',
            backgroundColor: '#D8090F',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
        });

        // Append buttons to buttons container
        buttonsContainer.appendChild(confirmButton);
        buttonsContainer.appendChild(cancelButton);

        // Append message and buttons to modal
        modal.appendChild(message);
        modal.appendChild(buttonsContainer);

        // Append modal to overlay
        overlay.appendChild(modal);

        // Append overlay to body
        document.body.appendChild(overlay);

        // Add event listeners
        confirmButton.addEventListener('click', () => {
            document.body.removeChild(overlay);
            resolve(true);
        });

        cancelButton.addEventListener('click', () => {
            document.body.removeChild(overlay);
            resolve(false);
        });
    });
}

/**
 * Main function to process routes, compile PDFs into a ZIP, and display progress.
 */
export async function exportMSG(): Promise<void> {
    let modalControls: {
        updateProgress: (percent: number) => void;
        updateStatus: (message: string) => void;
        close: () => void;
    } | null = null;

    // Cancellation flag
    let isCancelled = false;

    try {
        // Show confirmation popup
        const userConfirmed = await showConfirmationPopup();
        if (!userConfirmed) {
            console.log('User cancelled the operation.');
            return;
        }

        // URLs for external libraries
        const html2pdfURL = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        const jszipURL = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.0/jszip.min.js';
        const fileSaverURL = 'https://cdnjs.cloudflare.com/ajax/libs/FileSaver.js/2.0.5/FileSaver.min.js';

        // Load external libraries
        await loadScript(html2pdfURL);
        await loadScript(jszipURL);
        await loadScript(fileSaverURL);

        // Check if libraries are loaded
        if (!window.html2pdf) throw new Error('html2pdf failed to load.');
        if (!window.JSZip) throw new Error('JSZip failed to load.');
        if (!window.saveAs) throw new Error('FileSaver.js failed to load.');

        const zip = new window.JSZip();

        // Select all divs with class 'integration' and a 'data-route' attribute
        const routes: NodeListOf<HTMLDivElement> = document.querySelectorAll('div.integration[data-route]');
        const totalRoutes = routes.length;
        if (totalRoutes === 0) {
            alert('No routes found to process.');
            return;
        }
        console.log(`Found ${totalRoutes} integration(s).`);

        // Create and display the progress modal
        modalControls = createProgressModal();
        modalControls.updateProgress(0);
        modalControls.updateStatus(`Found ${totalRoutes} routes.`);

        // Add event listener for Cancel button
        const cancelButton = document.querySelector('#progress-modal button') as HTMLButtonElement;
        if (cancelButton) {
            cancelButton.addEventListener('click', () => {
                isCancelled = true;
                modalControls?.updateStatus('Cancellation requested. Stopping...');
                console.log('Cancellation requested by user.');
                // Optionally, disable the button to prevent multiple clicks
                cancelButton.disabled = true;
                cancelButton.style.cursor = 'not-allowed';
                cancelButton.style.opacity = '0.6';
            });
        }

        for (let i = 0; i < totalRoutes; i++) {
            if (isCancelled) {
                console.log('Process has been cancelled.');
                modalControls.updateStatus('Process cancelled by user.');
                break;
            }

            const integration: HTMLDivElement = routes[i];
            const dataRoute: string = integration.getAttribute('data-route') || `integration-${i + 1}`;
            console.log(`Processing integration ${i + 1}/${totalRoutes}: data-route="${dataRoute}"`);

            // Update modal status
            modalControls.updateStatus(`Processing ${i + 1} of ${totalRoutes}: "${camelCaseToSpaces(dataRoute.replaceAll('/MSG/', '').replaceAll('/', ' ➞ '))}"`);

            // Click the integration div
            integration.click();
            console.log(`Clicked on integration with data-route="${dataRoute}".`);

            // Wait for the new content to load (e.g., 3 seconds)
            const waitTime = 3000; // milliseconds
            console.log(`Waiting for ${waitTime / 1000} seconds for content to load...`);
            await delay(waitTime);

            if (isCancelled) {
                console.log('Process has been cancelled during wait.');
                modalControls.updateStatus('Process cancelled by user.');
                break;
            }

            // Select the .page-view element
            const pageView: HTMLElement | null = document.querySelector('.page-view');
            if (pageView) {
                console.log(`Generating PDF for data-route="${dataRoute}".`);
                // Update modal status
                modalControls.updateStatus(`Generating PDF for "${camelCaseToSpaces(dataRoute.replaceAll('/MSG/', '').replaceAll('/', '  ➞ '))}"...`);
                // Generate PDF blob
                const pdfBlob: Blob | null = await generatePDFBlob(pageView);
                if (pdfBlob) {
                    // Add the PDF blob to the ZIP archive
                    const filename: string = `${dataRoute}.pdf`;
                    zip.file(filename, pdfBlob);
                    console.log(`Added "${filename}" to ZIP.`);
                } else {
                    console.warn(`Failed to generate PDF for data-route="${dataRoute}".`);
                }
            } else {
                console.warn(`.page-view element not found after clicking data-route="${dataRoute}".`);
            }

            // Calculate and update progress
            const progressPercent = Math.round(((i + 1) / totalRoutes) * 100);
            modalControls.updateProgress(progressPercent);

            // Optional: Wait a bit before moving to the next integration to ensure stability
            const betweenWait = 1000; // milliseconds
            console.log(`Waiting for ${betweenWait / 1000} seconds before next integration.`);
            await delay(betweenWait);
        }

        if (!isCancelled) {
            // Update modal status
            modalControls.updateStatus('Generating ZIP file...');

            // Generate the ZIP file
            console.log('Generating ZIP file...');
            try {
                const zipBlob: Blob = await zip.generateAsync(
                    { type: 'blob' },
                    (metadata: any) => {
                        const percent = Math.round(metadata.percent);
                        console.log(`ZIP Progress: ${percent}%`);
                        modalControls?.updateProgress(percent);
                    }
                );
                // Trigger download of the ZIP file
                window.saveAs(zipBlob, 'MSG Export.zip');
                console.log('ZIP file generated and download initiated.');
                // Update modal status
                modalControls.updateStatus('Download initiated.');
                modalControls.updateProgress(100);
                // close modal after download
                modalControls.close();
            } catch (error) {
                console.error('Error generating ZIP file:', error);
                modalControls.updateStatus('Error generating ZIP file.');
            }
        } else {
            // If cancelled, optionally offer a partial ZIP
            if (zip.files && Object.keys(zip.files).length > 0) {
                try {
                    const zipBlob: Blob = await zip.generateAsync({ type: 'blob' });
                    window.saveAs(zipBlob, 'MSG Export_Partial.zip');
                    console.log('Partial ZIP file generated and download initiated.');
                    modalControls.updateStatus('Partial ZIP generated. Download initiated.');
                } catch (error) {
                    console.error('Error generating partial ZIP file:', error);
                    modalControls.updateStatus('Error generating partial ZIP file.');
                }
            } else {
                console.log('No PDFs were generated before cancellation.');
                modalControls.updateStatus('No PDFs were generated before cancellation.');
            }
        }
    } catch (error) {
        console.error("Error exporting MSG:", error);
        modalControls?.updateStatus('Error exporting MSG.');
    }
}